import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { srConsoleInputsData } from '../../data/srConsoleInputsData';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useFilterStore } from '../../store/filterStore';
import './SRSearchBar.scss';

interface SRSearchBarProps {
    tooltipPosition?: 'top' | 'bottom';
    useTooltip?: boolean;
    useAsStatic?: boolean;
    useIconAsButton?: boolean;
}

const SRSearchBar: React.FC<SRSearchBarProps> = ({
    useTooltip = true,
    tooltipPosition = 'top',
    useAsStatic = false,
    useIconAsButton = false
}) => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const isMobile = useIsMobile();
    const navigate = useNavigate();

    const { currentFilters, setFilters } = useFilterStore();

    const defaultFilters = {
        destination: [{ label: 'Antigua and Barbuda', value: 'antigua-and-barbuda' }],
        dates: [{ label: 'July 2025', value: '2024-07' }],
        guests: [{ label: '8 Guests', value: '8' }],
        yacht: ['Catamarans']
    };

    const formValues = currentFilters;

    const validateForm = () => {
        const requiredFields = ['destination', 'dates', 'guests', 'yacht'];
        return requiredFields.every(field => {
            const value = formValues[field];
            return Array.isArray(value) && value.length > 0;
        });
    };

    const handleSearch = async () => {
        const updatedFilters = { ...formValues };

        if (!validateForm()) {
            Object.keys(defaultFilters).forEach(key => {
                if (!updatedFilters[key]?.length) {
                    updatedFilters[key] = defaultFilters[key as keyof typeof defaultFilters];
                }
            });
        }

        setIsLoading(true);
        setFilters(updatedFilters);

        const params = new URLSearchParams({
            location: updatedFilters.destination?.map((item: any) => item.label).join(',') || '',
            guests: updatedFilters.guests?.[0]?.value || '',
            type: updatedFilters.yacht?.[0] || '',
            month: updatedFilters.dates?.[0]?.value || ''
        });

        setTimeout(() => {
            setIsLoading(false);
            navigate(`/search-result?${params.toString()}`);
        }, 2000);
    };

    return (
        <>
            <div className={`search-bar ${useAsStatic ? 'static' : ''}`}>
                {srConsoleInputsData.map((input, index) => (
                    <div
                        key={index}
                        className={`column ${index === srConsoleInputsData.length - 1 ? 'last' : ''} ${isMobile ? 'text-overflow' : ''}`}
                        onClick={input.clickHandler}
                        onFocus={() => setActiveIndex(index)}
                        onBlur={e => {
                            if (!e.currentTarget.contains(e.relatedTarget)) setActiveIndex(null);
                        }}
                    >
                        <label className="text-overflow" htmlFor={`sr-input-${index}`}>{input.title}</label>
                        <input
                            className="text-overflow"
                            type="text"
                            id={`sr-input-${index}`}
                            placeholder={input.placeholder}
                            value={
                                Array.isArray(formValues[input.key])
                                    ? formValues[input.key]
                                        .map((v: any) => (typeof v === 'string' ? v : v.label))
                                        .join(', ')
                                    : ''
                            }
                            readOnly
                        />
                        {(useTooltip && activeIndex === index) && (
                            <div className={`more-detail-tooltip ${tooltipPosition}`}>
                                <label>{input.tooltip}</label>
                            </div>
                        )}
                    </div>
                ))}
                <button
                    className="sr-button"
                    type="button"
                    onClick={handleSearch}
                    disabled={isLoading}
                >
                    {useIconAsButton ? (
                        <i className="fa-solid fa-magnifying-glass"></i>
                    ) : (
                        <>
                            {
                                isMobile ? 'Search' : (
                                    <i className="fa-solid fa-magnifying-glass"></i>
                                )}
                        </>
                    )}

                </button>
            </div>
            {isLoading && (
                <div className="loading-spinner">
                    <div className="spinner"></div>
                </div>
            )}
        </>
    );
};

export default SRSearchBar;
